/** Calendar date for display: yyyy-mm-dd. Uses the date portion of ISO strings to avoid TZ shift. */
export function formatCalendarDate(
  value: string | null | undefined,
  empty = '-',
): string {
  if (!value?.trim()) return empty
  const trimmed = value.trim()
  const part = trimmed.split('T')[0] ?? trimmed
  if (/^\d{4}-\d{2}-\d{2}$/.test(part)) return part
  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return empty
  const year = parsed.getFullYear()
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const day = String(parsed.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const CHICAGO_TZ = 'America/Chicago'

/** Chicago datetime for display: yyyy-mm-dd HH:MM:SS AM/PM. */
export function formatChicagoDateTime(
  value: string | number | Date | null | undefined,
  empty = '—',
): string {
  if (value == null || value === '') return empty
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return empty

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: CHICAGO_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).formatToParts(date)

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? ''

  const hour = get('hour').padStart(2, '0')
  const minute = get('minute').padStart(2, '0')
  const second = get('second').padStart(2, '0')
  const dayPeriod = get('dayPeriod').toUpperCase()

  return `${get('year')}-${get('month')}-${get('day')} ${hour}:${minute}:${second} ${dayPeriod}`
}

/** Local datetime for display: yyyy-mm-dd HH:MM (24-hour). */
export function formatLocalDateTime(
  value: string | number | null | undefined,
  empty = '-',
): string {
  if (value == null || value === '') return empty
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return empty
  const year = parsed.getFullYear()
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const day = String(parsed.getDate()).padStart(2, '0')
  const hours = String(parsed.getHours()).padStart(2, '0')
  const minutes = String(parsed.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}`
}
